import { useFocusEffect, useNavigation } from "@react-navigation/native";
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Animated,
  ImageBackground,
  StyleSheet,
  View,
  KeyboardAvoidingView,
} from "react-native";
import Image from "@components/common/Image";
import { TouchableOpacity } from "react-native-gesture-handler";
import LinearGradient from "react-native-linear-gradient";
import { useDispatch, useSelector } from "react-redux";
import Caret from "@assets/svg/Caret";
import WhatsappDefault from "@assets/svg/WhatsappDefault";
import Text from "@components/AppText";
import VideoPlayer from "@components/VideoPlayer";
import { STORY_EVENTS } from "@constants/analyticsEventsCT";
import color from "@constants/color";
import { actions as miscActions } from "@reducers/miscReducer";
import analytics from "@services/analytics";
import CloseIcon from "../../assets/svg/CloseIcon";
import metrics, { height, width } from "../../constants/metrics";
import handleCMSNavigation from "../../utils/handleCMSNavigation";
import { shareOnWhatsapp } from "../../utils/shareEntity";
import AnimatedBar from "./AnimatedBar";
import CircularLoader from "./CircularLoader";
import Poll from "./Polls/Poll";

import t from "@constants/localization";

const PATH = "screens/Story";

const StoryContainer = ({
  scrollForward,
  scrollBackward,
  data,
  isActive,
  isScrolling,
  currentList,
  submittedPolls,
  appInForeground,
}) => {
  const [currentImage, setCurrentImage] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isPaused, setIsPaused] = useState(false);

  const navigation = useNavigation();
  const dispatch = useDispatch();

  const [pollSubmitted, setPollSubmitted] = useState([]);

  const { user } = useSelector((state) => ({
    user: state.auth.user,
  }));

  const isShowWhatsApp = Boolean(
    data[currentImage] &&
      data[currentImage].sub_type !== "full_background_without_share" &&
      data[currentImage] &&
      data[currentImage]?.sharingTemplate &&
      data[currentImage]?.sharingTemplate.CX,
  );
  const isShowDetails = Boolean(
    data[currentImage] &&
      data[currentImage]?.link &&
      data[currentImage]?.link_type,
  );

  const noCTA = data[currentImage]?.sub_type === "full_background_no_cta";

  const updateViewedStories = (payload) => {
    dispatch(miscActions.updateViewedStories(payload));
  };
  useEffect(() => {
    if (isActive) {
      analytics.trackEvent(STORY_EVENTS.STORY_VIEWED, {
        list_name: data[currentImage]?.cl_title,
        story_name: data[currentImage]?.story_name,
        list_position: currentList,
        story_position: currentImage,
        share_button: !noCTA ? (isShowWhatsApp ? "yes" : "no") : "no",
        details_button: !noCTA ? (isShowDetails ? "yes" : "no") : "no",
      });
      if (currentImage === data.length - 1) {
        updateViewedStories([parseInt(data[0]?.id)]);
      }
    }
  }, [currentImage, isActive]);

  useFocusEffect(
    useCallback(() => {
      setIsPaused(false);
    }, []),
  );

  useEffect(() => {
    setIsPaused(false);
  }, [appInForeground]);

  const previousStory = () => {
    if (currentImage > 0) {
      setCurrentImage(currentImage - 1);
    } else {
      scrollBackward();
    }
  };

  const nextStory = () => {
    if (currentImage < data.length - 1) {
      setCurrentImage(currentImage + 1);
    } else {
      scrollForward();
    }
  };

  const setIsPausedIsTrue = () => {
    if (isPaused) {
      setIsPaused(false);
    }
  };

  const handleWhatAppShare = () => {
    setIsPaused(true);
    if (data[currentImage]?.sharingTemplate?.CL?.image_link) {
      shareOnWhatsapp({
        message: `${data[currentImage].sharingTemplate.CL.template.replace(
          "$$leaderName",
          user.name,
        )} \n ${data[currentImage]?.sharingLink || ""}`,
        image: data[currentImage]?.sharingTemplate?.CL?.image_link,
      });
      analytics.trackEvent(STORY_EVENTS.STORY_SHARED, {
        list_name: data[currentImage].cl_title,
      });
    } else if (data[currentImage]?.sharingTemplate?.CL) {
      shareOnWhatsapp({
        message: data[currentImage].sharingTemplate.CL.template.replace(
          "$$leaderName",
          user.name,
        ),
      });

      analytics.trackEvent(STORY_EVENTS.STORY_SHARED, {
        list_name: data[currentImage]?.cl_title,
        story_name: data[currentImage]?.story_name,
        list_position: data[currentImage]?.list_position,
        story_position: currentImage,
        current_list_position: currentList,
      });
    }
  };

  const handleCmsEntity = () => {
    analytics.trackEvent(STORY_EVENTS.STORY_SEE_DETAILS, {
      list_name: data[currentImage]?.cl_title,
      story_name: data[currentImage]?.story_name,
      list_position: data[currentImage]?.list_position,
      story_position: currentImage,
      current_list_position: currentList,
    });
    setIsPaused(true);
    handleCMSNavigation(
      data[currentImage]?.link,
      data[currentImage]?.link_type ?? "INTERNAL",
    );
  };

  const handlePollEvent = (data) => {
    const { pollId, answerId } = data;
    analytics.trackEvent(STORY_EVENTS.STORY_POLL_ANSWERED, {
      list_name: data[currentImage]?.cl_title,
      story_name: data[currentImage]?.story_name,
      list_position: data[currentImage]?.list_position,
      story_position: currentImage,
      current_list_position: currentList,
      pollId,
      answerId,
    });
  };

  const isPoll = !!data[currentImage]?.poll_id;
  const isPollSubmitted =
    isPoll &&
    Array.isArray(submittedPolls) &&
    submittedPolls.includes(data[currentImage]?.poll_id);
  const pollData = data[currentImage]?.poll;

  return (
    <View style={styles.container}>
      <View style={styles.container}>
        {(data[currentImage]?.sub_type === "full_background" ||
          data[currentImage]?.sub_type === "full_background_no_cta" ||
          data[currentImage]?.sub_type === "full_background_without_share") && (
          <ImageBackground
            resizeMode="cover"
            source={{
              uri: data[currentImage].image,
            }}
            blurRadius={5}
            fadeDuration={0}
            style={styles.image}
          >
            <Image
              onLoadStart={() => setIsLoading(true)}
              onLoadEnd={() => setIsLoading(false)}
              fadeDuration={0}
              onError={() => {
                console.log(data[currentImage].image);
              }}
              resizeMode={Image.resizeMode.contain}
              source={{
                uri: data[currentImage]?.image,
              }}
              style={{
                marginTop: 20,
                flex: 1,
                marginBottom: 80,
              }}
            />
          </ImageBackground>
        )}
        {data[currentImage]?.sub_type === "video_card_with_details" &&
          isActive && (
            <VideoPlayer
              muted={isLoading}
              paused={isScrolling || isPaused}
              onLoadStart={() => setIsLoading(true)}
              onLoad={() => setIsLoading(false)}
              isSecondPlayer
              resizeMode="contain"
              videoLink={data[currentImage].image}
            />
          )}
        {isLoading && (
          <View style={styles.loaderContainer}>
            <CircularLoader />
          </View>
        )}
        <LinearGradient
          start={{ x: 0, y: 0 }}
          end={{ x: 0, y: 1 }}
          colors={["rgba(0, 0, 0, 0.3)", "rgba(0, 0, 0, 0)"]}
          style={{
            height: height / 5,
            position: "absolute",
            top: 0,
            bottom: 0,
            left: 0,
            right: 0,
          }}
        />

        <View style={styles.hiddenButtonContainer}>
          <KeyboardAvoidingView
            style={styles.actionButtonContainer}
            behavior="height"
          >
            <TouchableOpacity
              delayLongPress={200}
              style={styles.actionButton}
              onLongPress={() => setIsPaused(true)}
              onPress={() => previousStory()}
              onPressOut={setIsPausedIsTrue}
            />
            {isPoll && (
              <Poll
                {...pollData}
                trackEvent={handlePollEvent}
                pauseStory={() => setIsPaused(true)}
                unpauseStory={() => setIsPaused(false)}
                submitted={
                  pollSubmitted.includes(data[currentImage]?.poll_id) ||
                  isPollSubmitted
                }
                setSubmitted={setPollSubmitted}
              />
            )}
            <TouchableOpacity
              delayLongPress={200}
              style={styles.actionButton}
              onLongPress={() => setIsPaused(true)}
              onPress={() => nextStory()}
              onPressOut={setIsPausedIsTrue}
            />
          </KeyboardAvoidingView>

          <BottomButtonBar
            {...{
              noCTA,
              isShowDetails,
              isShowWhatsApp,
              handleCmsEntity,
              handleWhatAppShare,
            }}
          />
        </View>
        <View style={styles.headerContainer}>
          <View
            style={{
              flexDirection: "row",
              height: 2,
            }}
          >
            {data.map((_, index) => (
              <View
                key={index.toString()}
                style={{
                  marginRight: 5,
                }}
              >
                {isActive && (
                  <AnimatedBar
                    index={index}
                    pause={isLoading || isPaused || isScrolling}
                    currentImage={currentImage}
                    duration={data[currentImage]?.duration}
                    onCompleteAnimation={() => nextStory()}
                    maxWidth={
                      (metrics.screenWidth - 30) / Math.max(data.length, 1) - 5
                    }
                  />
                )}
              </View>
            ))}
          </View>

          {isActive && <CloseButton onPress={navigation.goBack} />}
        </View>
      </View>
    </View>
  );
};

export default StoryContainer;

const CloseButton = ({ onPress }) => {
  const fadeAnim = useRef(new Animated.Value(0)).current;
  React.useEffect(() => {
    Animated.timing(fadeAnim, {
      toValue: 1,
      duration: 100,
      useNativeDriver: true,
    }).start();
  }, [fadeAnim]);
  return (
    <Animated.View
      style={{
        opacity: fadeAnim,
      }}
    >
      <TouchableOpacity
        onPress={onPress}
        style={{
          padding: 3,
          marginTop: metrics.baseGutter,
        }}
      >
        <CloseIcon width={18} height={18} fill="#fff" />
      </TouchableOpacity>
    </Animated.View>
  );
};

const BottomButtonBar = ({
  isShowDetails,
  isShowWhatsApp,
  noCTA,
  handleCmsEntity,
  handleWhatAppShare,
}) => {
  return (
    <LinearGradient
      style={[
        styles.shadowBox,
        {
          justifyContent:
            isShowDetails && isShowWhatsApp ? "space-between" : "center",
        },
      ]}
      start={{ x: 0, y: 0 }}
      end={{ x: 0, y: 1 }}
      colors={["rgba(0, 0, 0, 0)", "rgba(0, 0, 0, 0.6)"]}
    >
      {!noCTA && isShowDetails && (
        <TouchableOpacity
          style={[
            styles.ctaButton,
            {
              backgroundColor: "white",
              width: !isShowWhatsApp ? width - 30 : (width - 30) / 2 - 10,
            },
          ]}
          onPress={handleCmsEntity}
        >
          <Text
            fontType="head2"
            bold
            style={{ color: color.primary, fontSize: 20 }}
          >
            {t(`${PATH}/viewDetails`)}
          </Text>
          <Caret strokeColor={color.primary} />
        </TouchableOpacity>
      )}
      {!noCTA && isShowWhatsApp && (
        <TouchableOpacity
          style={[
            styles.ctaButton,
            {
              backgroundColor: "#23CF5F",
              width: !isShowDetails ? width - 30 : (width - 30) / 2 - 10,
            },
          ]}
          onPress={handleWhatAppShare}
        >
          <WhatsappDefault size={25} />
          <Text
            fontType="head2"
            bold
            style={{ color: "#fff", marginLeft: 5, fontSize: 20 }}
          >
            {t(`${PATH}/share`)}
          </Text>
        </TouchableOpacity>
      )}
    </LinearGradient>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "black",
  },
  image: {
    ...StyleSheet.absoluteFill,
    width: null,
    height: null,
  },
  headerContainer: {
    position: "absolute",
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    marginTop: 10,
    marginLeft: 15,
    // padding: metrics.baseGutter,
    paddingTop: 10,
    zIndex: 20,
  },
  hiddenButtonContainer: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    width: "100%",
    height: "100%",
  },
  actionButton: {
    height: "100%",
    width: metrics.screenWidth / 2,
  },
  ctaButton: {
    flex: 1,
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: 10,
    paddingVertical: 12,
  },
  loaderContainer: {
    position: "absolute",
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "transparent",
  },
  shadowBox: {
    height: 80,
    width,
    flexDirection: "row",
    padding: metrics.baseGutter,
    // justifyContent: "center",
  },
  actionButtonContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    flex: 1,
  },
});
